'use client';

import { useEffect, useRef, useState } from 'react';
import { usePathname } from '@/i18n/navigation';
import { getShellState } from '@/lib/getShellState';
import type { ShellState } from '@/types/shellState';

/**
 * The in-flight `getShellState` request, if any, so the shell components that
 * all want the new page's facts share one round trip.
 *
 * Only *in-flight* requests are shared: the entry is cleared the moment the
 * promise settles. A resolved response is never handed to a later caller —
 * a component that mounts afterwards (an overlay, a modal) must see the shell
 * as it is then, and a cache keyed only by pathname would re-serve a response
 * that an editor's publish has since invalidated.
 */
let inFlight: { pathname: string; promise: Promise<ShellState> } | null = null;

function shellStateFor(pathname: string): Promise<ShellState> {
  if (inFlight?.pathname === pathname) {
    return inFlight.promise;
  }
  const entry: { pathname: string; promise: Promise<ShellState> } = {
    pathname,
    promise: undefined as unknown as Promise<ShellState>,
  };
  entry.promise = getShellState(pathname).finally(() => {
    if (inFlight === entry) {
      inFlight = null;
    }
  });
  inFlight = entry;
  return entry.promise;
}

/**
 * Matelso swaps every `tel:` number on the page for a tracking number
 * (60.06), and `MatelsoPhoneLinkRewriter` rescans the DOM on each
 * `usePathname()` change. The shell's new numbers arrive a round trip *after*
 * that rescan, so without a second pass they keep the untracked number for
 * the rest of the visit.
 *
 * The rescan rewrites **what it finds in the DOM**, so it is worthless until
 * React has committed the new numbers. It is therefore fired from an effect
 * on the applied value (effects run after commit), never from the promise
 * callback that sets it: a `setTimeout(…, 0)` queued there loses the race
 * against React's concurrent scheduler, which is free to commit in a later
 * task. Measured on STA — both rescans of a Standort→Standort navigation ran
 * while the header still held the previous Standort's number, and the new
 * number stayed raw.
 *
 * The timeout that remains is only a dedup window, so the Header, Footer and
 * Sticky CTA Bar applying the same response cost one rescan, not three.
 */
let rescanScheduled = false;
function scheduleMatelsoRescan() {
  if (rescanScheduled) {
    return;
  }
  rescanScheduled = true;
  setTimeout(() => {
    rescanScheduled = false;
    window.__matelsoRescan?.();
  }, 0);
}

/**
 * Keeps one slice of the page shell in step with the page the visitor is on.
 *
 * The `(main)` layout resolves the shell (menu, phone pair, top bar label,
 * Sticky CTA Bar) for the pathname it server-renders and hands each component
 * its slice as a prop. Next.js does not re-run that layout on a client-side
 * navigation, so without this hook every one of those values would stay at
 * what the *first* page the visitor landed on resolved — a Standort's phone
 * number would follow them onto a page belonging to a different Standort.
 *
 * `initial` is the server-rendered slice, so the first paint needs no request
 * and hydration matches. From the first navigation onwards the slice comes
 * from `getShellState`, picked out by `select`.
 */
export function useShellState<T>(
  initial: T,
  select: (state: ShellState) => T,
): T {
  const pathname = usePathname();
  const [value, setValue] = useState(initial);

  // `select` is written inline at every call site, so it is a new function on
  // every render; the effect must key on the pathname alone or it would
  // re-fetch on each one.
  const selectRef = useRef(select);
  selectRef.current = select;

  // The server already resolved this pathname — the props are its answer.
  const isInitialRender = useRef(true);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    let cancelled = false;

    shellStateFor(pathname)
      .then((state) => {
        if (!cancelled) {
          setValue(selectRef.current(state));
        }
      })
      .catch(console.error);

    return () => {
      cancelled = true;
    };
  }, [pathname]);

  // After the commit that put the new numbers on screen — see
  // `scheduleMatelsoRescan`. `value` only ever changes when a response is
  // applied, so this does not fire on an unrelated re-render; the first run
  // is the server-rendered value, which `MatelsoPhoneLinkRewriter` has
  // already scanned on mount.
  const isInitialValue = useRef(true);
  useEffect(() => {
    if (isInitialValue.current) {
      isInitialValue.current = false;
      return;
    }
    scheduleMatelsoRescan();
  }, [value]);

  return value;
}
